/**
 * Tela de acompanhamento (R19.6): linha do tempo por etapa em linguagem clara e
 * reflexo contínuo do status (polling). Resiliente a instabilidade de rede
 * (estado "reconectando") e a erros persistentes (R19.8), sempre com uma ação
 * de retomada — a aplicação nunca "trava".
 *
 * Acessibilidade (R19.7): a mudança de status é anunciada via região
 * `aria-live="polite"`; cada etapa expõe seu estado por texto (não só cor).
 */
import type { JobStatusResponse } from "../../lib/apiClient";
import type { PollingPhase } from "../../hooks/useJobPolling";
import {
  plainStatus,
  STAGE_DEFS,
  stageStates,
  type JobStatus,
  type StageState,
} from "../../lib/status";
import { Alert } from "../Alert";
import { Button } from "../Button";
import { CheckIcon, RefreshIcon, SpinnerIcon } from "../Icons";

interface ProgressScreenProps {
  readonly job: JobStatusResponse | null;
  readonly phase: PollingPhase;
  readonly error: string | null;
  readonly onRetry: () => void;
  readonly onStartOver: () => void;
}

const STATE_TEXT: Record<StageState, string> = {
  pending: "aguardando",
  active: "em andamento",
  complete: "concluído",
  failed: "com problema",
};

export function ProgressScreen({ job, phase, error, onRetry, onStartOver }: ProgressScreenProps) {
  const status: JobStatus = job?.status ?? "RECEIVED";
  const attempts = job?.attempt_count ?? 0;
  const info = plainStatus(status, attempts);
  const states = stageStates(status);

  return (
    <section className="mp-card" aria-labelledby="progress-title">
      <h1 className="mp-card__title" id="progress-title">
        Convertendo seu documento
      </h1>
      <p className="mp-card__subtitle">
        Você pode manter esta página aberta. Vamos atualizar automaticamente.
      </p>

      <div
        className={`mp-status${phase === "reconnecting" ? " mp-status--reconnecting" : ""}`}
        aria-live="polite"
      >
        <span className="mp-status__icon">
          {phase === "reconnecting" ? <SpinnerIcon size={24} /> : <SpinnerIcon size={24} />}
        </span>
        <span>
          <span className="mp-status__title">
            {phase === "reconnecting" ? "Reconectando…" : info.title}
          </span>
          <br />
          <span className="mp-status__detail">
            {phase === "reconnecting"
              ? "A conexão oscilou. Estamos tentando novamente."
              : info.detail}
          </span>
        </span>
      </div>

      <ol className="mp-stepper">
        {STAGE_DEFS.map((stage) => {
          const state = states[stage.id];
          return (
            <li key={stage.id} className={`mp-step mp-step--${state}`}>
              <span className="mp-step__marker" aria-hidden="true">
                {state === "complete" ? (
                  <CheckIcon size={18} />
                ) : state === "active" ? (
                  <SpinnerIcon size={18} />
                ) : state === "failed" ? (
                  "!"
                ) : null}
              </span>
              <span className="mp-step__body">
                <span className="mp-step__label">
                  {stage.label}
                  <span className="sr-only"> — {STATE_TEXT[state]}</span>
                </span>
                <span className="mp-step__desc">{stage.description}</span>
              </span>
            </li>
          );
        })}
      </ol>

      {phase === "error" ? (
        <>
          <Alert tone="error" title="Acompanhamento interrompido">
            {error ?? "Não foi possível obter o status agora."}
          </Alert>
          <div className="mp-actions">
            <Button variant="primary" onClick={onRetry}>
              <RefreshIcon size={18} /> Tentar novamente
            </Button>
            <Button variant="ghost" onClick={onStartOver}>
              Enviar outro documento
            </Button>
          </div>
        </>
      ) : null}
    </section>
  );
}
